import { Button, Heading, Span } from '@pcln/horizon'
import { RecentSelection } from '@pcln/horizon-customer-growth-components'
import { theme } from 'pcln-design-system'
import { useEffect, type Dispatch, type SetStateAction } from 'react'
import { useMediaQuery } from 'react-responsive'

import ComponentMetricsBoundary from '@/components/ComponentMetricsBoundary/ComponentMetricsBoundary'
import SimilarHotels from '@/components/TripActivity/SimilarHotels'
import useBootstrapData from '@/hooks/useBootstrapData'
import useSeti from '@/hooks/useSeti'
import useSimilarHotels from '@/hooks/useSimilarHotels'

import { fireRecentSelectionLoad } from './ga4'
import useTripActivityData from './hooks/useTripActivityData'
import RecentSearchCarousel from './RecentSearchCarousel'
import { recentSelectionMapper } from './recentSelectionMapper'
import { tripActivityVariants } from './TripActivity.variant'
import type { AbandonedSelection as AbandonedSelectionType, SearchProductList } from './types'
import { onViewAllButtonClick } from './utils'

export default function TripActivity({
  productType,
  setIsVisible,
  shouldUseSmallerHeading,
  abandonedSelectionData,
  hideAbandonedSelection,
  pageName,
}: {
  setIsVisible?: Dispatch<SetStateAction<boolean>>
  productType: SearchProductList
  shouldUseSmallerHeading?: boolean
  abandonedSelectionData?: AbandonedSelectionType | null
  hideAbandonedSelection?: boolean
  pageName: 'homepage' | 'landing'
}) {
  const {
    appName,
    appVersion,
    isMobile,
    webstats: { clientGUID: cguid },
  } = useBootstrapData()

  const isDesktop = useMediaQuery({
    query: `(min-width: ${theme.breakpoints.lg as string})`,
  })
  const { atMostFiveRecentSearchCards, abandonedSelection } = useTripActivityData(
    productType,
    abandonedSelectionData,
  )

  const abandonedHotelSelection = abandonedSelection?.productType === 'hotels' ? abandonedSelection : null
  const appCode = isMobile ? 'MOBILEWEB' : 'DESKTOP'

  const isQdpVariant =
    useSeti({
      experimentCode: 'WEB_HP_HOTELSELECTIONS_QDP',
      shouldFireImpression: !hideAbandonedSelection && abandonedHotelSelection !== null,
    }) === 'VARIANT'

  const showSimilarHotels =
    !hideAbandonedSelection && abandonedHotelSelection !== null && pageName === 'homepage'
  const { similarHotels, isLoading: isSimilarHotelsLoading } = useSimilarHotels({
    showSimilarHotels,
    abandonedHotelSelection,
    appCode,
    appName,
    appVersion,
    cguid,
  })
  const showAbandonedSelection = abandonedSelection && !hideAbandonedSelection

  const isHomepage = pageName === 'homepage'

  useSeti({
    experimentCode: 'WEB_HP_PACKAGE_SELECTIONS',
    shouldFireImpression: Boolean(showAbandonedSelection) && productType?.includes('PKG'),
  })

  const hasRecentSearchCards = atMostFiveRecentSearchCards.length > 0
  const showContinueBrowsingHeading = isHomepage && hasRecentSearchCards && Boolean(showAbandonedSelection)
  const showContinueBrowsingHeadingSeti =
    useSeti({
      experimentCode: 'WEB_HP_RECENTSEARCH_SUBHEADING',
      shouldFireImpression: showContinueBrowsingHeading,
    }) === 'DEFAULT' && showContinueBrowsingHeading

  useEffect(() => {
    if (setIsVisible) {
      setIsVisible(hasRecentSearchCards || abandonedSelection !== null)
    }
  }, [hasRecentSearchCards, abandonedSelection, setIsVisible, hideAbandonedSelection])

  useEffect(() => {
    if (showAbandonedSelection) {
      fireRecentSelectionLoad({ selection: abandonedSelection, pageName })
    }
  }, [showAbandonedSelection, abandonedSelection, pageName])

  const { heading, recentSearchesHeading, wrapperSlot, viewAllButton } = tripActivityVariants({
    useSmallerHeading: shouldUseSmallerHeading,
  })
  return hasRecentSearchCards || showAbandonedSelection ? (
    <ComponentMetricsBoundary componentId='TripActivity'>
      <div className='relative'>
        <div className={wrapperSlot()}>
          <Heading as='h2' palette='neutral' shade='13' className={heading()}>
            Pick up where you left off
          </Heading>
          {isDesktop && (
            <Button
              type='secondary'
              size='sm'
              slots={{ buttonSlot: viewAllButton() }}
              data-testid='view-all-button-top-right'
              onClick={() => onViewAllButtonClick(pageName)}
            >
              View all recent activity
            </Button>
          )}
        </div>
        <div className='flex flex-col gap-10 md:gap-12'>
          {(showAbandonedSelection || (isHomepage && hasRecentSearchCards)) && (
            <div className='flex flex-col gap-6'>
              {showAbandonedSelection && (
                <>
                  <div className='flex flex-col gap-2'>
                    <RecentSelection
                      {...recentSelectionMapper({
                        selection: abandonedSelection,
                        ga4Context: { pageName },
                        isQdpVariant,
                      })}
                    />
                    {abandonedHotelSelection && (
                      <Span palette='neutral' shade='13' className='text-body3'>
                        *Availability and pricing are subject to change
                      </Span>
                    )}
                  </div>
                </>
              )}
              {isHomepage && hasRecentSearchCards && (
                <div className='flex flex-col gap-4'>
                  {showContinueBrowsingHeadingSeti && (
                    <Heading as='h3' palette='neutral' shade='13' className={recentSearchesHeading()}>
                      Or continue browsing
                    </Heading>
                  )}
                  <RecentSearchCarousel items={atMostFiveRecentSearchCards} pageName={pageName} />
                </div>
              )}
            </div>
          )}
          {!isHomepage && hasRecentSearchCards && (
            <div className='flex flex-col gap-4'>
              {showAbandonedSelection && (
                <Heading as='h3' palette='neutral' shade='13' className={recentSearchesHeading()}>
                  Your recent searches
                </Heading>
              )}
              <RecentSearchCarousel items={atMostFiveRecentSearchCards} pageName={pageName} />
            </div>
          )}
          {isHomepage && similarHotels.length > 0 && (
            <SimilarHotels
              similarHotels={similarHotels}
              isLoading={isSimilarHotelsLoading}
              pageName={pageName}
              useSmallerHeading={shouldUseSmallerHeading}
              abandonedHotelName={abandonedHotelSelection?.headline}
            />
          )}
        </div>
        {!isDesktop && (
          <Button
            type='secondary'
            size='sm'
            className='mt-4 w-full'
            data-testid='view-all-button-bottom'
            onClick={() => onViewAllButtonClick(pageName)}
          >
            View all recent activity
          </Button>
        )}
      </div>
    </ComponentMetricsBoundary>
  ) : null
}
